<template>
	<div class="mb-4">
		<div class="flex flex-wrap items-start justify-between gap-4">
			<div class="flex items-start min-w-0 gap-4">
				<div
					class="w-[72px] h-[72px] bg-center bg-cover rounded-full border border-gray-200"
					:style="applicantAvatarStyle"
				/>
				<div class="min-w-0">
					<div class="flex flex-wrap items-center gap-2">
						<h2
							class="m-0 text-3xl font-black tracking-wide uppercase text-neutral-900"
						>
							{{ applicantFullName }}
						</h2>
						<ApplicantProfileDeactivatedBadge v-if="isOrgContext" :application="application" />
						<TopFitBadge
							v-if="showTopFitBadge"
							:variant="topFitBadgeVariant"
						/>
					</div>

					<div
						class="flex flex-wrap items-center gap-4 mt-1 text-sm text-neutral-500"
					>
						<!-- Email -->
						<span
							v-if="applicantEmail"
							class="inline-flex items-center gap-2"
						>
							<a
								:href="emailHref"
								class="inline-flex items-center gap-2 hover:text-neutral-900"
								:aria-label="
									$t('general.applicant-email-aria-label', {
										email: applicantEmail
									})
								"
							>
								<IconEnvelope size="14" />
								{{ applicantEmail }}
							</a>
						</span>

						<!-- Phone -->
						<span
							v-if="applicantPhone"
							class="inline-flex items-center gap-2"
						>
							<a
								:href="phoneHref"
								class="inline-flex items-center gap-2 hover:text-neutral-900"
								:aria-label="
									$t('general.applicant-phone-aria-label', {
										phone: applicantPhone
									})
								"
							>
								<IconPhone size="14" />
								{{ applicantPhone }}
							</a>
						</span>

						<!-- LinkedIn profile link -->
						<a
							v-if="applicantProfileLink"
							:href="applicantProfileLink"
							target="_blank"
							rel="noopener noreferrer"
							class="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"
						>
							<IconExternalLink size="14" />
							{{ $t('general.view-linkedin-profile') }}
						</a>
					</div>
				</div>
			</div>

			<ApplicationActionButtons
				v-if="showActionButtons"
				:application="application"
				:job-offer="jobOffer"
				:is-org="isOrgContext"
				:loading="applicationLoading"
				:show-invite="!application"
				variant="full"
				@invite="handleInvite"
				@update="handleUpdate"
				@contact="handleContact"
			/>
		</div>
	</div>
</template>

<script>
import ApplicantProfileDeactivatedBadge from '~/components/Applications/ApplicantProfileDeactivatedBadge.vue'
import { TopFitBadge } from '~/components/Applications'
import ApplicationActionButtons from './ApplicationActionButtons.vue'
import {
	IconPhone,
	IconEnvelope,
	IconExternalLink
} from '~/components/Icons'

export default {
	name: 'ApplicantProfileHeader',
	components: {
		ApplicantProfileDeactivatedBadge,
		ApplicationActionButtons,
		TopFitBadge,
		IconPhone,
		IconEnvelope,
		IconExternalLink
	},
	props: {
		applicantProfileId: {
			type: [String, Number],
			required: true
		},
		jobOfferId: {
			type: [String, Number],
			default: null
		},
		applicantAvatarStyle: {
			type: Object,
			required: true
		},
		applicantFullName: {
			type: String,
			required: true
		},
		applicantEmail: {
			type: String,
			default: null
		},
		applicantPhone: {
			type: String,
			default: null
		},
		applicantProfileLink: {
			type: String,
			default: null
		},
		showTopFitBadge: {
			type: Boolean,
			default: false
		},
		showActionButtons: {
			type: Boolean,
			default: false
		},
		application: {
			type: Object,
			default: null
		},
		jobOffer: {
			type: Object,
			default: null
		},
		isOrgContext: {
			type: Boolean,
			default: false
		},
		applicationLoading: {
			type: [Boolean, Array],
			default: false
		}
	},
	computed: {
		topFitBadgeVariant() {
			return this.application?.status === 'denied' ? 'declined' : 'default'
		},
		emailHref() {
			if (!this.applicantEmail) {
				return null
			}

			return `mailto:${this.applicantEmail.trim()}`
		},
		phoneHref() {
			if (!this.applicantPhone) {
				return null
			}

			// Keep only characters supported by tel URI.
			const normalizedPhone = this.applicantPhone
				.trim()
				.replace(/[^+\d().\-\s]/g, '')
				.replace(/\s+/g, '')

			return `tel:${normalizedPhone}`
		}
	},
	methods: {
		handleInvite() {
			this.$emit('invite', this.applicantProfileId, this.jobOfferId)
		},
		handleUpdate(applicationId, status, sendRejectionEmail, rejectionResult) {
			this.$emit(
				'update',
				applicationId,
				status,
				sendRejectionEmail,
				...(rejectionResult ? [rejectionResult] : [])
			)
		},
		handleContact() {
			if (this.application?.id) {
				this.$emit('contact', this.application.id)
			}
		}
	}
}
</script>
